import React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@shared/ui/dialog";
import { ValueLabel } from "@features/ax/shared/ValueLabel";
import { Badge } from "@shared/ui/badge";
import { Card, CardContent } from "@shared/ui/card";
import { Check, X, Box } from "lucide-react";

interface ResourceType {
  id: string;
  name: string;
  description: string;
  activities: string;
  cycles: boolean;
  routes: boolean;
  validation: boolean;
  inventory: boolean;
  svcOrders: boolean;
  status: boolean;
}

interface ResourceTypeDetailDialogProps {
  isOpen: boolean;
  onClose: () => void;
  data?: ResourceType;
}

export function ResourceTypeDetailDialog({
  isOpen,
  onClose,
  data,
}: ResourceTypeDetailDialogProps) {
  if (!data) return null;

  const activitiesList = data.activities 
    ? data.activities.split(",").map((a) => a.trim()).filter(Boolean)
    : [];

  const capabilities = {
    cycles: data.cycles,
    routes: data.routes,
    validation: data.validation,
    inventory: data.inventory,
    svcOrders: data.svcOrders,
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader className="border-b pb-3">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <Box className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-semibold text-gray-900">
                Resource type detail
              </DialogTitle>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-6 pt-4">
          {/* Basic Info */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-400 tracking-wider">
              Basic info
            </h3>
            <Card className="border-gray-200 shadow-none bg-gray-50/30">
              <CardContent className="p-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                <ValueLabel label="Name" value={data.name} />
                <ValueLabel
                  label="Status"
                  value={
                    <Badge
                      variant="outline"
                      className={`px-2 py-0.5 text-xs font-semibold inline-flex items-center gap-1.5 ${
                        data.status
                          ? "text-green-700 bg-green-50 border-green-200"
                          : "text-gray-500 bg-gray-50 border-gray-200"
                      }`}
                    >
                      {data.status && <span className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse" />}
                      {data.status ? "Active" : "Inactive"}
                    </Badge>
                  }
                />
                <div className="md:col-span-2">
                  <ValueLabel
                    label="Description"
                    value={
                      <p className="text-sm text-gray-600 leading-relaxed">
                        {data.description || "No description provided."}
                      </p>
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Capabilities */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-400 tracking-wider">
              Capabilities
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              {Object.entries(capabilities).map(([key, val]) => (
                <Card
                  key={key}
                  className="border-gray-200 shadow-none bg-gray-50/30"
                >
                  <CardContent className="p-3 flex flex-col justify-between h-20">
                    <span className="text-xs font-medium text-gray-500">
                      {key.replace(/([A-Z])/g, " $1").toLowerCase()}
                    </span>
                    {val ? (
                      <div className="flex items-center gap-1 text-green-600 font-medium">
                        <span className="p-0.5 bg-green-50 text-green-600 rounded-full">
                          <Check className="w-3.5 h-3.5" />
                        </span>
                        <span className="text-xs font-semibold text-green-700">Supported</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1 text-gray-400 font-medium">
                        <span className="p-0.5 bg-gray-50 text-gray-400 rounded-full border border-gray-300">
                          <X className="w-3 h-3 text-gray-400" />
                        </span>
                        <span className="text-xs font-semibold text-gray-400">Disabled</span>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Supported Activities */}
          <div className="space-y-3">
            <h3 className="text-xs font-semibold text-gray-400 tracking-wider">
              Supported activities
            </h3>
            <Card className="border-gray-200 shadow-none bg-gray-50/30">
              <CardContent className="p-4">
                {activitiesList.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {activitiesList.map((activity, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="px-3 py-1 text-xs font-medium text-blue-600 bg-blue-50 border-blue-100"
                      >
                        {activity}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <span className="text-sm text-gray-500 italic">No activities supported.</span>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
